/**
 * Emergency Access Dashboard (Public)
 *
 * Allows emergency responders to access patient data via QR code or token
 * No authentication required - public endpoint with consent tracking
 */

import {
  AlertCircle,
  Building,
  FileText,
  Loader2,
  Mail,
  Phone,
  Shield,
  User,
} from "lucide-react";
import React, { useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import apiClient from "../../services/api/apiClient";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { i18nToast } from "../../utils/i18nToast";
import type { EmergencyProfileData } from "../../types/emergency";

/**
 * Coded values (FHIR criticality, severity, clinical status, gender) stay as
 * sent; only the word shown to the responder is translated. Unknown codes are
 * shown as they are rather than hidden.
 */
function codeLabel(code: string | undefined | null, t: Translation): string {
  if (!code) return t.unknown;
  const labels: Record<string, string> = {
    high: t.emergxCodeHigh,
    low: t.emergxCodeLow,
    "unable-to-assess": t.emergxCodeUnableToAssess,
    mild: t.emergxCodeMild,
    moderate: t.emergxCodeModerate,
    severe: t.emergxCodeSevere,
    active: t.emergxCodeActive,
    inactive: t.emergxCodeInactive,
    resolved: t.emergxCodeResolved,
    remission: t.emergxCodeRemission,
    recurrence: t.emergxCodeRecurrence,
    relapse: t.emergxCodeRelapse,
    male: t.male,
    female: t.female,
    other: t.other,
    unknown: t.unknown,
  };
  return labels[code] ?? code;
}

interface ResponderInfo {
  name: string;
  organization: string;
  professionalId?: string;
  email?: string;
  phone?: string;
  accessReason: string;
}

const EmergencyAccessDashboard: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [step, setStep] = useState<"scanner" | "token" | "responder" | "data">(
    "scanner",
  );
  const [accessToken, setAccessToken] = useState("");
  const [responderInfo, setResponderInfo] = useState<ResponderInfo>({
    name: "",
    organization: "",
    professionalId: "",
    email: "",
    phone: "",
    accessReason: "",
  });
  const [emergencyData, setEmergencyData] =
    useState<EmergencyProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Extract token from URL if present
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl =
      urlParams.get("token") || window.location.pathname.split("/").pop();

    if (tokenFromUrl && tokenFromUrl.length > 20) {
      setAccessToken(tokenFromUrl);
      setStep("responder");
    }
  }, []);

  const handleAccessRequest = async () => {
    if (!accessToken) {
      setError(t.pleaseEnterToken);
      return;
    }

    if (!responderInfo.name || !responderInfo.organization) {
      setError(t.pleaseProvideResponderInfo);
      return;
    }

    if (!consentAccepted) {
      setError(t.pleaseAcceptConsent);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post("/emergency/access/token", {
        accessToken,
        responderInfo,
      });

      setEmergencyData(response.data.data.emergencyData);
      setStep("data");
      i18nToast.success(t.emergencyDataAccessGranted);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message || t.accessTokenInvalid;
      setError(errorMessage);
      i18nToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-red-600 text-white py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">{t.emergencyDataAccess}</h1>
              <p className="text-red-100">{t.forEmergencyRespondersOnly}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              aria-label={t.close}
              className="text-red-600 hover:text-red-800 dark:text-red-400"
            >
              ×
            </button>
          </div>
        )}

        {/* Step 1: QR Scanner or Token Input */}
        {step === "scanner" && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
              {t.scanQROrEnterToken}
            </h2>

            {/* QR Scanner Placeholder */}
            <div className="mb-8">
              <div className="aspect-square max-w-md mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                <div className="text-center p-6">
                  <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {t.qrScannerPlaceholder}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    {t.qrScannerInstructions}
                  </p>
                </div>
              </div>
            </div>

            {/* Manual Token Input */}
            <div className="text-center mb-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t.orEnterTokenManually}
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-4">
              <input
                type="text"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={t.enterAccessToken}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 font-mono"
              />

              <button
                onClick={() => accessToken && setStep("responder")}
                disabled={!accessToken}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.continue}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Responder Information */}
        {step === "responder" && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t.responderInformation}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t.responderInformationDescription}
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAccessRequest();
              }}
              className="space-y-4"
            >
              {/* Name (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.fullName} <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={responderInfo.name}
                    onChange={(e) =>
                      setResponderInfo({
                        ...responderInfo,
                        name: e.target.value,
                      })
                    }
                    placeholder={t.enterYourFullName}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Organization (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.organization} <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={responderInfo.organization}
                    onChange={(e) =>
                      setResponderInfo({
                        ...responderInfo,
                        organization: e.target.value,
                      })
                    }
                    placeholder={t.hospitalOrganizationName}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Professional ID (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.professionalId}{" "}
                  <span className="text-gray-500">({t.optional})</span>
                </label>
                <input
                  type="text"
                  value={responderInfo.professionalId}
                  onChange={(e) =>
                    setResponderInfo({
                      ...responderInfo,
                      professionalId: e.target.value,
                    })
                  }
                  placeholder={t.licenseBadgeNumber}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>

              {/* Email (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.email}{" "}
                  <span className="text-gray-500">({t.optional})</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={responderInfo.email}
                    onChange={(e) =>
                      setResponderInfo({
                        ...responderInfo,
                        email: e.target.value,
                      })
                    }
                    placeholder={t.yourEmail}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Phone (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.phone}{" "}
                  <span className="text-gray-500">({t.optional})</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={responderInfo.phone}
                    onChange={(e) =>
                      setResponderInfo({
                        ...responderInfo,
                        phone: e.target.value,
                      })
                    }
                    placeholder={t.yourPhone}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Access Reason (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.reasonForAccess} <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    required
                    value={responderInfo.accessReason}
                    onChange={(e) =>
                      setResponderInfo({
                        ...responderInfo,
                        accessReason: e.target.value,
                      })
                    }
                    placeholder={t.briefDescriptionEmergency}
                    rows={3}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <label
                  htmlFor="emergency-consent"
                  className="flex items-start gap-3 cursor-pointer"
                  aria-label={t.emergencyAccessConsent}
                >
                  <input
                    id="emergency-consent"
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 text-red-600 rounded border-gray-300"
                    aria-label={t.emergxConsentCheckboxAria}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {t.emergencyAccessConsent}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t.emergencyAccessConsentText}
                    </p>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep("scanner")}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t.back}
                </button>
                <button
                  type="submit"
                  disabled={loading || !consentAccepted}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.accessing}
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      {t.accessEmergencyData}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Emergency Data Display */}
        {step === "data" && emergencyData && (
          <div className="space-y-6">
            {/* Patient Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t.patientInformation}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <DataItem label={t.name} value={emergencyData.patient.name} />
                <DataItem
                  label={t.age}
                  value={t.emergxAgeYears.replace(
                    "{n}",
                    String(emergencyData.patient.age),
                  )}
                />
                <DataItem
                  label={t.gender}
                  value={codeLabel(emergencyData.patient.gender, t)}
                />
                <DataItem
                  label={t.bloodType}
                  value={emergencyData.patient.bloodType || t.unknown}
                  critical
                />
                <DataItem
                  label={t.height}
                  value={
                    emergencyData.patient.height
                      ? fmt.quantity("height", emergencyData.patient.height)
                      : t.unknown
                  }
                />
                <DataItem
                  label={t.weight}
                  value={
                    emergencyData.patient.weight
                      ? fmt.quantity("weight", emergencyData.patient.weight)
                      : t.unknown
                  }
                />
              </div>
            </div>

            {/* Critical Alerts */}
            {emergencyData.allergies.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-600 dark:border-red-500 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <h3 className="text-lg font-bold text-red-900 dark:text-red-100">
                    {t.criticalAllergies}
                  </h3>
                </div>
                <div className="space-y-3">
                  {emergencyData.allergies.map((allergy, i) => (
                    <div
                      key={i}
                      className="p-3 bg-white dark:bg-gray-800 rounded border border-red-300 dark:border-red-700"
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {allergy.allergen}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {codeLabel(allergy.criticality, t)} |{" "}
                        {allergy.reactionType}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medications */}
            <DataSection
              title={t.currentMedications}
              emptyText={t.emergxNotAvailable.replace(
                "{title}",
                t.currentMedications,
              )}
              items={emergencyData.medications}
              renderItem={(med) => (
                <>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {med.medicationName}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {med.dosage} | {med.frequency}
                  </p>
                </>
              )}
            />

            {/* Medical Conditions */}
            <DataSection
              title={t.medicalConditions}
              emptyText={t.emergxNotAvailable.replace(
                "{title}",
                t.medicalConditions,
              )}
              items={emergencyData.conditions}
              renderItem={(cond) => (
                <>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {cond.condition}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {codeLabel(cond.severity, t)} |{" "}
                    {codeLabel(cond.clinicalStatus, t)}
                  </p>
                </>
              )}
            />

            {/* Emergency Contacts */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t.emergencyContacts}
              </h3>
              {emergencyData.emergencyContacts.length > 0 ? (
                <div className="space-y-3">
                  {emergencyData.emergencyContacts.map((contact, i) => (
                    <div
                      key={i}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {contact.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {contact.relationship}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        <strong>{t.phone}:</strong> {contact.phone}
                      </p>
                      {contact.email && (
                        <p className="text-sm text-gray-900 dark:text-white">
                          <strong>{t.email}:</strong> {contact.email}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  {t.noEmergencyContactsAvailable}
                </p>
              )}
            </div>

            {/* Disclaimer */}
            <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400">
              <p>
                <strong>{t.disclaimer}:</strong> {t.emergencyDataDisclaimerText}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const DataItem: React.FC<{
  label: string;
  value: string;
  critical?: boolean;
}> = ({ label, value, critical }) => (
  <div>
    <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
    <p
      className={`text-base font-medium ${critical ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}
    >
      {value}
    </p>
  </div>
);

function DataSection<T>({
  title,
  emptyText,
  items,
  renderItem,
}: {
  title: string;
  emptyText: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {title} ({items.length})
      </h3>
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              {renderItem(item)}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400">{emptyText}</p>
      )}
    </div>
  );
}

export default EmergencyAccessDashboard;
